<template>
  <WdsStack
    v-if="
      (expensesStore.enableExpenseReports && paymentRequestStore.isEditable) ||
      (!paymentRequestStore.isEditable && expensesStore.expenseReports.length > 0)
    "
    gap-md
    direction-column
  >
    <WdsStack justify-space-between align-end class="tw-flex-grow">
      <div>
        <WdsHeading type="subtitle">{{ $t('Expenses') }}</WdsHeading>
        <WdsSpinner v-if="!disabled && isLoadingAvailableExpenseReports" indeterminate></WdsSpinner>
        <span
          v-else-if="!disabled && hasAvailableExpenseReports && showExpenseReportSelection"
          class="tw-text-gray-600"
        >
          {{ $t('Attach pending expenses for this job') }}
        </span>
      </div>
      <ExpenseReportModal
        v-if="!disabled && !isLoadingAvailableExpenseReports && !showExpenseReportSelection"
        v-slot="{ triggerModal }"
        :hire-id="paymentRequestStore.hireLocalId"
        @report-created="handleCreatedExpenseReport"
      >
        <WdsButton data-test="create-expense-report-button" @click="triggerModal">
          <WdsStack align-center gap-sm>
            <WdsIcon name="plus" :size="16" />
            {{ $t('New expense report') }}
          </WdsStack>
        </WdsButton>
      </ExpenseReportModal>
    </WdsStack>
    <Transition name="fade">
      <WdsStack
        v-if="hasAvailableExpenseReports && showExpenseReportSelection"
        justify-space-between
        align-end
        class="tw-flex-grow"
      >
        <template v-if="disabled">
          <!-- eslint-disable-next-line vue/require-v-for-key -- Baseline eslint ignore please fix if working on this-->
          <div v-for="report in expensesStore.expenseReports" class="tw-ml-1">
            {{ report.title }} ({{ paymentRequestStore.currency }}
            {{
              currencyJs(report.total, {
                ...paymentRequestStore.currencyConfig,
                fromCents: true,
              }).format()
            }})
          </div>
        </template>
        <WdsSelect
          v-else
          v-model="expenseReportsModel"
          data-test="expense-report-select"
          class="tw-w-3/4 tw-max-w-full tw-pr-2"
          multi-select
          :list="selectOptions"
          :placeholder="$t('Select expense reports')"
        />
        <ExpenseReportModal
          v-if="!disabled && !isLoadingAvailableExpenseReports"
          v-slot="{ triggerModal }"
          :hire-id="paymentRequestStore.hireLocalId"
          @report-created="handleCreatedExpenseReport"
        >
          <WdsButton data-test="create-expense-report-button" @click="triggerModal">
            <WdsStack align-center gap-sm>
              <WdsIcon name="plus" :size="16" />
              {{ $t('New expense report') }}
            </WdsStack>
          </WdsButton>
        </ExpenseReportModal>
      </WdsStack>
    </Transition>
    <WdsStack data-test="expense-report-list" direction-column gap-md>
      <ExpenseReport
        v-for="report in expensesStore.expenseReports"
        :key="report.localId"
        :expense-report="report"
        :disabled="disabled"
        @expense-report-updated="updateSelectableExpenseReports"
        @expense-report-unlinked="handleExpenseReportUnlinked"
        @expense-report-deleted="updateSelectableExpenseReports"
        @expenses-updated="updateSelectableExpenseReports"
      ></ExpenseReport>
    </WdsStack>
  </WdsStack>
</template>

<script setup lang="ts">
import {
  WdsButton,
  WdsHeading,
  WdsIcon,
  WdsSelect,
  WdsSpinner,
  WdsStack,
} from '@worksome/wds-components'
import { computed, ComputedRef, ref, watch } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import currencyJs from 'currency.js'
import { usePaymentRequestStore } from '@/components/PaymentRequest/store/usePaymentRequestStore'
import { useExpensesStore } from '@/components/PaymentRequest/store/useExpensesStore'
import { useTranslation } from '@/composables/translation'
import ExpenseReportModal from '@/components/ModalExpenseReport.vue'
import ExpenseReport from '@/components/PaymentRequest/Expenses/ExpenseReport.vue'
import { ExpenseReport as ExpenseReportType } from '@/components/PaymentRequest/types'

const { $t } = useTranslation()

const paymentRequestStore = usePaymentRequestStore()
const expensesStore = useExpensesStore()

const showExpenseReportSelection = ref(false)
const hasLoadedSelectableExpenseReports = ref(false)

const {
  result: resultExpenseReports,
  loading: isLoadingAvailableExpenseReports,
  refetch: updateSelectableExpenseReports,
} = useQuery(gql`
  query expenseReportsQuery {
    me {
      activeAccount {
        ... on Freelancer {
          expenseReports {
            globalId
            localId
            title
            description
            bill_id
            total
            isDraft
            hire_id
            createdAt
            expenses {
              globalId
              localId
              vendor
              description
              amount
              currency
              expenseAt
              files {
                globalId
                localId
                name
                storageName
                icon {
                  icon
                  color
                  thumbnail
                }
              }
            }
          }
        }
      }
    }
  }
`)

watch(
  () => resultExpenseReports?.value?.me?.activeAccount?.expenseReports,
  (expenseReports) => {
    if (hasLoadedSelectableExpenseReports.value) return
    if (Array.isArray(expenseReports) && !hasLoadedSelectableExpenseReports.value) {
      hasLoadedSelectableExpenseReports.value = true
      // if any expense reports dont have a bill_id already, then showExpenseReportSelection.
      showExpenseReportSelection.value = expenseReports.some(
        (expenseReport) => !Number(expenseReport.bill_id),
      )
    }
  },
)

const availableExpenseReports: ComputedRef<ExpenseReportType[]> = computed(() =>
  resultExpenseReports?.value?.me?.activeAccount?.expenseReports?.filter(
    (expenseReport) =>
      !Number(expenseReport.bill_id) ||
      Number(expenseReport.bill_id) === paymentRequestStore.billId,
  ),
)

const disabled = computed(() => !paymentRequestStore.isEditable)

const lookupAvailableExpenseReportsToSelectOptions = computed(() =>
  availableExpenseReports.value?.map((availableExpenseReport) => ({
    expenseReport: {
      globalId: availableExpenseReport.globalId,
      localId: availableExpenseReport.localId,
      title: availableExpenseReport.title,
      description: availableExpenseReport.description,
      total: availableExpenseReport.total,
      expenses: availableExpenseReport.expenses,
    },
    selectOption: {
      value: availableExpenseReport.localId,
      label: availableExpenseReport.title,
    },
  })),
)

const selectOptions = computed(() =>
  lookupAvailableExpenseReportsToSelectOptions.value.map((lookupItem) => lookupItem.selectOption),
)

const hasAvailableExpenseReports = computed(() => !!availableExpenseReports.value?.length)

const expenseReportsModel = computed({
  get: (): { value: string | number; label: string }[] =>
    expensesStore.expenseReports.map((expenseReport) => ({
      value: expenseReport.localId,
      label: expenseReport.title,
    })),
  set: (updatedSelection) => {
    const selectedValues = updatedSelection.map((selectedOption) => selectedOption.value)
    expensesStore.updateExpenseReports(
      lookupAvailableExpenseReportsToSelectOptions.value
        ?.filter((lookupItem) => selectedValues?.includes(lookupItem.selectOption.value))
        .map((lookupItem) => lookupItem.expenseReport),
    )
  },
})

const handleCreatedExpenseReport = (expenseReport: any) => {
  expensesStore.addExpenseReport(expenseReport)
  updateSelectableExpenseReports()
}

const handleExpenseReportUnlinked = (expenseReport: any) => {
  updateSelectableExpenseReports()
  showExpenseReportSelection.value = true
}
</script>
